import React, { useEffect, useState } from 'react';
import {
  Modal,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useDispatch } from 'react-redux';
import Icon from 'react-native-vector-icons/Ionicons';

import { AppDispatch } from '@/store';
import { switchPunch } from '@/store/slices/timeClockSlice';
import { colors, font, space, radius } from '@/theme/chalkCourt';

export interface SwitchTarget {
  punchId: number;
  /** Display time of the punch being switched, e.g. "12:29 PM". */
  time: string;
  /** Its direction today. */
  wasClockIn: boolean;
  /** What the switch does, e.g. "9:58 AM – 12:29 PM becomes a 2 hr 31 min shift." */
  effect?: string;
}

interface Props {
  target: SwitchTarget | null;
  onClose: () => void;
  onSwitched: (message: string) => void;
}

/**
 * Confirm switching a punch between clock-in and clock-out. The time doesn't
 * change. It's written PENDING (a manager signs off behind the scenes).
 */
const SwitchPunchModal: React.FC<Props> = ({ target, onClose, onSwitched }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (target) {
      setReason('');
      setError(null);
    }
  }, [target?.punchId]);

  if (!target) return null;
  const to = target.wasClockIn ? 'clock-out' : 'clock-in';
  const from = target.wasClockIn ? 'clock-in' : 'clock-out';

  const submit = async () => {
    setBusy(true);
    setError(null);
    const result = await dispatch(
      switchPunch({ punchId: target.punchId, reason: reason.trim() || undefined })
    );
    setBusy(false);
    if (switchPunch.fulfilled.match(result) && result.payload.success) {
      onSwitched(result.payload.message);
      onClose();
    } else {
      const msg = switchPunch.fulfilled.match(result) ? result.payload.message : (result.payload as string);
      setError(msg || "Couldn't switch the punch. Try again.");
    }
  };

  return (
    <Modal visible animationType="slide" transparent onRequestClose={onClose}>
      <KeyboardAvoidingView
        style={styles.backdrop}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <View style={styles.sheet}>
          <View style={styles.handle} />
          <View style={styles.headRow}>
            <Text style={styles.title}>SWITCH PUNCH</Text>
            <TouchableOpacity
              onPress={onClose}
              accessibilityLabel="close"
              hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
              <Icon name="close" size={24} color={colors.inkSoft} />
            </TouchableOpacity>
          </View>

          <View style={styles.swap}>
            <View style={styles.swapSide}>
              <Text style={styles.swapTime}>{target.time}</Text>
              <Text style={styles.swapKind}>{from}</Text>
            </View>
            <Icon name="arrow-forward" size={20} color={colors.accent} />
            <View style={styles.swapSide}>
              <Text style={styles.swapTime}>{target.time}</Text>
              <Text style={[styles.swapKind, styles.swapKindNew]}>{to}</Text>
            </View>
          </View>
          {!!target.effect && <Text style={styles.effect}>{target.effect}</Text>}

          <Text style={styles.label}>REASON (OPTIONAL)</Text>
          <TextInput
            style={styles.input}
            value={reason}
            onChangeText={setReason}
            placeholder={`e.g. Meant to ${target.wasClockIn ? 'clock out' : 'clock in'}`}
            placeholderTextColor={colors.inkFaint}
            accessibilityLabel="Reason"
            maxLength={120}
          />

          <Text style={styles.note}>The time stays the same.</Text>

          {error && (
            <View style={styles.errorBox}>
              <Icon name="alert-circle" size={16} color={colors.accentDark} />
              <Text style={styles.errorText}>{error}</Text>
            </View>
          )}

          <TouchableOpacity
            style={[styles.submit, busy && styles.submitBusy]}
            activeOpacity={0.85}
            disabled={busy}
            onPress={submit}>
            <Text style={styles.submitText}>{busy ? 'SWITCHING…' : `SWITCH TO ${to.toUpperCase()}`}</Text>
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(28,26,18,0.45)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: colors.bg,
    borderTopLeftRadius: radius.lg,
    borderTopRightRadius: radius.lg,
    paddingHorizontal: space.lg,
    paddingBottom: space.xl,
  },
  handle: {
    alignSelf: 'center',
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.lineStrong,
    marginTop: space.sm,
    marginBottom: space.md,
  },
  headRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontFamily: font.display,
    fontSize: 30,
    color: colors.ink,
    letterSpacing: -0.5,
  },
  swap: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.line,
    paddingVertical: space.md,
    marginTop: space.md,
  },
  swapSide: {
    alignItems: 'center',
  },
  swapTime: {
    fontFamily: font.displayBold,
    fontSize: 22,
    color: colors.ink,
  },
  swapKind: {
    fontFamily: font.bodySemi,
    fontSize: 13,
    color: colors.inkSoft,
    marginTop: 2,
  },
  swapKindNew: {
    color: colors.accentDark,
  },
  effect: {
    fontFamily: font.bodySemi,
    fontSize: 13.5,
    color: colors.inkSoft,
    marginTop: space.sm,
    textAlign: 'center',
  },
  label: {
    fontFamily: font.bodyBold,
    fontSize: 11,
    letterSpacing: 1.6,
    color: colors.inkFaint,
    marginBottom: space.xs,
    marginTop: space.md,
  },
  input: {
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.line,
    paddingHorizontal: space.md,
    paddingVertical: space.md,
    fontFamily: font.bodyMed,
    fontSize: 16,
    color: colors.ink,
  },
  note: {
    fontFamily: font.body,
    fontSize: 12.5,
    color: colors.inkSoft,
    marginTop: space.md,
  },
  errorBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.accentSoft,
    borderRadius: radius.sm,
    padding: space.sm,
    marginTop: space.md,
  },
  errorText: {
    flex: 1,
    marginLeft: space.xs,
    fontFamily: font.bodyMed,
    fontSize: 13,
    color: colors.accentDark,
  },
  submit: {
    height: 56,
    borderRadius: radius.md,
    backgroundColor: colors.accent,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: space.lg,
  },
  submitBusy: {
    opacity: 0.5,
  },
  submitText: {
    fontFamily: font.display,
    fontSize: 22,
    letterSpacing: 1,
    color: colors.onAccent,
  },
});

export default SwitchPunchModal;
